import React from 'react';
import { Link } from 'react-router-dom';
import { useDemoContext } from '../../contexts/DemoContext';
import { Bell, AlertTriangle, XCircle } from 'lucide-react';

export default function DemoNotificationsPage() {
  const { notifications, markNotificationAsRead, markAllNotificationsAsRead } = useDemoContext();

  const unreadNotifications = notifications.filter(n => !n.read);

  const getNotificationIcon = (type) => {
    if (type === 'policy_expired') return XCircle;
    if (type === 'expiration_warning') return AlertTriangle;
    return Bell;
  };

  const getNotificationColor = (type) => {
    if (type === 'policy_expired') return 'text-red-500';
    if (type === 'expiration_warning') return 'text-yellow-500';
    return 'text-blue-500';
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
          <p className="text-gray-600 mt-1">
            {unreadNotifications.length} unread notification{unreadNotifications.length !== 1 ? 's' : ''}
          </p>
        </div>
        {unreadNotifications.length > 0 && (
          <button
            onClick={markAllNotificationsAsRead}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className="card">
        <div className="divide-y divide-gray-200">
          {notifications.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <Bell className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium text-gray-700">No notifications yet</p>
              <p className="text-sm text-gray-500 mt-2">
                Create a policy that expires in 30 days, 14 days, or today to generate demo alerts.
              </p>
            </div>
          ) : (
            notifications.map((notification) => {
              const Icon = getNotificationIcon(notification.notification_type);
              const iconColor = getNotificationColor(notification.notification_type);

              return (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer ${
                    !notification.read ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => !notification.read && markNotificationAsRead(notification.id)}
                >
                  <div className="flex items-start">
                    <Icon className={`h-5 w-5 ${iconColor} mr-3 flex-shrink-0 mt-0.5`} />
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-600'}`}>
                          {notification.title}
                        </h3>
                        {!notification.read && (
                          <span className="w-2 h-2 bg-primary-600 rounded-full ml-2"></span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">{notification.message}</p>
                      <div className="flex items-center gap-4 mt-2">
                        <p className="text-xs text-gray-500">
                          {new Date(notification.created_at).toLocaleDateString()}
                        </p>
                        {notification.link_url && (
                          <Link
                            to={notification.link_url}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            View Policy →
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>In Demo Mode:</strong> These alerts are generated from your current demo policies and keep their read state for this session.
          In the full product, the same 30/14/0-day windows also drive morning email reminders to the right team members.
        </p>
      </div>
    </div>
  );
}
